// ABOUTME: Translated labels of the bulk change reason codes: skip reasons, dropped content types, apply reasons.
// ABOUTME: Each hook returns a label function; an unknown code falls back to the raw code (a refused delete's message).

import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { DroppedContentTypeReason, SkipReason } from '../types';

/** A skip reason's label; the detail (the refusal message of a refused delete) follows it. */
export function useSkipReasonLabel() {
    const { formatMessage } = useIntl();
    const label = useCallback(
        (reason: SkipReason | string): string => {
            switch (reason) {
                case 'NO_WRITE_PERMISSION':
                    return formatMessage({
                        id: 'findReplace.skipReason.noWritePermission',
                        defaultMessage: 'You cannot edit this node',
                    });
                case 'NODE_READONLY':
                    return formatMessage({
                        id: 'findReplace.skipReason.nodeReadonly',
                        defaultMessage: 'The node is read-only',
                    });
                case 'DEALERSHIP_READONLY':
                    return formatMessage({
                        id: 'findReplace.skipReason.dealershipReadonly',
                        defaultMessage: 'The node is read-only for dealerships',
                    });
                case 'FIELD_READONLY':
                    return formatMessage({
                        id: 'findReplace.skipReason.fieldReadonly',
                        defaultMessage: 'The field is read-only',
                    });
                case 'REGEX_TIMEOUT':
                    return formatMessage({
                        id: 'findReplace.skipReason.regexTimeout',
                        defaultMessage: 'The search took too long on this value',
                    });
                case 'CASCADE_TOO_LARGE':
                    return formatMessage({
                        id: 'bulkChange.skipReason.cascadeTooLarge',
                        defaultMessage: 'Its cascade deletes too many objects at once',
                    });
                case 'DELETE_REFUSED':
                    return formatMessage({
                        id: 'bulkChange.skipReason.deleteRefused',
                        defaultMessage: 'The delete would be refused',
                    });
                default:
                    return reason;
            }
        },
        [formatMessage],
    );
    return useCallback(
        (reason: SkipReason | string, detail?: string | null): string =>
            detail ? `${label(reason)}: ${detail}` : label(reason),
        [label],
    );
}

export function useDroppedReasonLabel() {
    const { formatMessage } = useIntl();
    return useCallback(
        (reason: DroppedContentTypeReason): string => {
            switch (reason) {
                case 'NOT_FOUND':
                    return formatMessage({
                        id: 'findReplace.droppedReason.notFound',
                        defaultMessage: 'it does not exist in this hierarchy',
                    });
                case 'UNSUPPORTED_TYPE':
                    return formatMessage({
                        id: 'findReplace.droppedReason.unsupportedType',
                        defaultMessage: 'this action cannot change a field of its type',
                    });
                case 'READ_ONLY':
                    return formatMessage({
                        id: 'findReplace.droppedReason.readOnly',
                        defaultMessage: 'it is read-only',
                    });
                case 'CALCULATED':
                    return formatMessage({
                        id: 'findReplace.droppedReason.calculated',
                        defaultMessage: 'it is calculated',
                    });
                case 'ENCRYPTED':
                    return formatMessage({
                        id: 'findReplace.droppedReason.encrypted',
                        defaultMessage: 'it is encrypted',
                    });
                case 'NO_VIEW_ACCESS':
                    return formatMessage({
                        id: 'findReplace.droppedReason.noViewAccess',
                        defaultMessage: 'you cannot view it',
                    });
                default:
                    return reason;
            }
        },
        [formatMessage],
    );
}

const SKIP_REASONS: string[] = [
    'NO_WRITE_PERMISSION',
    'NODE_READONLY',
    'DEALERSHIP_READONLY',
    'FIELD_READONLY',
    'REGEX_TIMEOUT',
    'CASCADE_TOO_LARGE',
    'DELETE_REFUSED',
];

/**
 * The apply reason is a conflict reason, a skip reason code, or free text: the issues upsertNodes
 * reported about the node (a company rule refusing it), or the message of a refused delete, shown
 * as it is.
 */
export function useApplyReasonLabel(deleteRun = false) {
    const { formatMessage } = useIntl();
    const skipReasonLabel = useSkipReasonLabel();
    return useCallback(
        (reason: string): string => {
            switch (reason) {
                case 'VALUE_CHANGED':
                    return formatMessage({
                        id: 'findReplace.applyReason.valueChanged',
                        defaultMessage: 'Value changed after the scan',
                    });
                case 'NOW_INHERITED':
                    return formatMessage({
                        id: 'findReplace.applyReason.nowInherited',
                        defaultMessage: 'Now inherited from the parent',
                    });
                case 'MISSING':
                    return deleteRun
                        ? formatMessage({
                              id: 'bulkChange.applyReason.objectMissing',
                              defaultMessage: 'The object no longer exists',
                          })
                        : formatMessage({
                              id: 'findReplace.applyReason.missing',
                              defaultMessage: 'The value no longer exists',
                          });
                case 'NOT_MODIFIED':
                    return formatMessage({
                        id: 'bulkChange.applyReason.notModified',
                        defaultMessage: 'Not written by the save',
                    });
                case 'CONCURRENCY_ERROR':
                    return formatMessage({
                        id: 'bulkChange.applyReason.concurrency',
                        defaultMessage: 'Edited by someone else during the apply',
                    });
                case 'DELETED_WITH_ANCESTOR':
                    return formatMessage({
                        id: 'bulkChange.applyReason.deletedWithAncestor',
                        defaultMessage: 'Deleted with its parent object',
                    });
                case 'DELETED_BY_CASCADE':
                    return formatMessage({
                        id: 'bulkChange.applyReason.deletedByCascade',
                        defaultMessage: 'Deleted by the cascade of another object',
                    });
                case 'ANCESTOR_NOT_DELETED':
                    return formatMessage({
                        id: 'bulkChange.applyReason.ancestorNotDeleted',
                        defaultMessage: 'Its parent object could not be deleted',
                    });
                default:
                    if (SKIP_REASONS.includes(reason)) return skipReasonLabel(reason);
                    return deleteRun
                        ? reason
                        : formatMessage({
                              id: 'bulkChange.applyReason.companyRule',
                              defaultMessage: 'Refused by a company rule',
                          });
            }
        },
        [deleteRun, formatMessage, skipReasonLabel],
    );
}
